// Supabase Action Approval Modal
// Displays pending Supabase actions (SQL, create table, etc.) for user approval

import { useState } from 'react';
import { AlertTriangle, Check, X, Database, Code2, Table2, Shield, Terminal, FileText, UserCog, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { PxMark } from '@/components/PxMark';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { toast } from 'sonner';

export interface SupabaseAction {
    id: string;
    type: 'supabase-sql' | 'supabase-create-table' | 'supabase-alter-table' | 'supabase-policy' | 'supabase-edge-function' | 'supabase-logs' | 'supabase-schema' | 'supabase-admin-operation' | 'supabase-auth-users' | 'supabase-email-template';
    name?: string;
    table?: string;
    source?: string;
    limit?: number;
    // For admin operations
    operation?: 'insert' | 'update' | 'delete' | 'select' | 'create' | 'list';
    content: string; // SQL, JSON, or code
}

interface SupabaseActionApprovalProps {
    action: SupabaseAction;
    onApprove: (result: any) => void;
    onReject: () => void;
    onClose: () => void;
}

export function SupabaseActionApproval({ action, onApprove, onReject, onClose }: SupabaseActionApprovalProps) {
    const {
        executeSql,
        listTables,
        getSchema,
        listEdgeFunctions,
        getLogs,
        executeAdminAction,
        manageAuthUsers,
        updateEmailTemplate,
        deployEdgeFunction
    } = useSupabaseMCP();
    const [isExecuting, setIsExecuting] = useState(false);

    // Get action icon and label (couleur : encre partout, err pour le bypass RLS)
    const getActionInfo = () => {
        switch (action.type) {
            case 'supabase-sql':
                return { icon: Database, color: 'text-foreground', label: 'Exécuter SQL' };
            case 'supabase-create-table':
                return { icon: Table2, color: 'text-foreground', label: 'Créer une table' };
            case 'supabase-alter-table':
                return { icon: Table2, color: 'text-foreground', label: 'Modifier une table' };
            case 'supabase-policy':
                return { icon: Shield, color: 'text-foreground', label: 'Créer une policy RLS' };
            case 'supabase-edge-function':
                return { icon: Code2, color: 'text-foreground', label: 'Déployer une Edge Function' };
            case 'supabase-logs':
                return { icon: Terminal, color: 'text-foreground', label: 'Lire les logs' };
            case 'supabase-schema':
                return { icon: FileText, color: 'text-foreground', label: 'Voir le schéma' };
            case 'supabase-admin-operation':
                return { icon: Lock, color: 'text-err', label: 'Admin DB (bypass RLS)' };
            case 'supabase-auth-users':
                return { icon: UserCog, color: 'text-foreground', label: 'Gestion des utilisateurs' };
            case 'supabase-email-template':
                return { icon: Mail, color: 'text-foreground', label: 'Configuration des emails' };
            default:
                return { icon: Database, color: 'text-foreground', label: 'Action Supabase' };
        }
    };

    const { icon: ActionIcon, color, label } = getActionInfo();

    // Check if this is a destructive action
    const isDestructive = () => {
        const lowerContent = action.content.toLowerCase();
        if (action.type === 'supabase-admin-operation' && (action.operation === 'delete' || action.operation === 'update')) return true;
        if (action.type === 'supabase-auth-users' && action.operation === 'delete') return true;

        return lowerContent.includes('drop') ||
            lowerContent.includes('truncate') ||
            lowerContent.includes('delete from');
    };

    const handleApprove = async () => {
        setIsExecuting(true);

        try {
            let result;

            switch (action.type) {
                case 'supabase-sql':
                case 'supabase-create-table':
                case 'supabase-alter-table':
                case 'supabase-policy':
                    result = await executeSql(action.content);
                    break;
                case 'supabase-logs':
                    result = await getLogs(action.source, action.limit);
                    break;
                case 'supabase-schema':
                    result = await getSchema();
                    break;
                case 'supabase-edge-function':
                    {
                        // Parsed content might be raw code or generic description
                        // For now we support updating metadata/deploying via name
                        // Real code deployment needs file upload handling which is complex here
                        // We assume content contains JSON config if possible, or we just init
                        let config = {};
                        try { config = JSON.parse(action.content); } catch (e) { }

                        result = await deployEdgeFunction({
                            slug: action.name || 'new-function',
                            name: action.name || 'New Function',
                            // Default to secure
                            verify_jwt: true,
                            ...config
                        });
                    }
                    break;
                case 'supabase-admin-operation':
                    {
                        let data = {};
                        try { data = JSON.parse(action.content); } catch (e) { }
                        // Execute admin action
                        result = await executeAdminAction(
                            action.table || 'users',
                            (action.operation as any) || 'select',
                            data
                        );
                    }
                    break;
                case 'supabase-auth-users':
                    {
                        let userData = {};
                        try { userData = JSON.parse(action.content); } catch (e) { }
                        result = await manageAuthUsers(
                            (action.operation as any) || 'list',
                            userData
                        );
                    }
                    break;
                case 'supabase-email-template':
                    {
                        let config = {};
                        try { config = JSON.parse(action.content); } catch (e) { }
                        result = await updateEmailTemplate(config);
                    }
                    break;
                default:
                    throw new Error(`Unknown action type: ${action.type}`);
            }

            if (result.success) {
                toast.success('Action exécutée', {
                    description: 'L\'action Supabase a été exécutée avec succès.',
                });
                onApprove(result.data);
            } else {
                toast.error('Erreur', {
                    description: result.error || 'L\'action a échoué.',
                });
            }
        } catch (error: any) {
            toast.error('Erreur', {
                description: error.message || 'Une erreur est survenue.',
            });
        } finally {
            setIsExecuting(false);
            onClose();
        }
    };

    const handleReject = () => {
        toast('Action annulée', {
            description: 'L\'action Supabase a été refusée.',
        });
        onReject();
        onClose();
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="border-hairline-strong bg-card sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className={`flex items-center gap-2 ${color}`}>
                        <ActionIcon className="h-5 w-5" />
                        {label}
                        {action.name && <span className="font-mono text-[13px] font-normal text-muted-foreground">: {action.name}</span>}
                    </DialogTitle>
                    <DialogDescription>
                        L'agent Pixel souhaite exécuter cette action sur votre Supabase.
                    </DialogDescription>
                </DialogHeader>

                {/* Destructive warning */}
                {isDestructive() && (
                    <div className="flex items-center gap-3 border-l-2 border-err bg-err/5 p-3">
                        <AlertTriangle className="h-4 w-4 shrink-0 text-err" />
                        <p className="text-sm text-foreground">
                            <span className="font-medium">Attention :</span> cette action peut supprimer des données de manière irréversible.
                        </p>
                    </div>
                )}

                {/* Code preview */}
                <div className="relative">
                    <pre className="max-h-80 overflow-x-auto border border-border bg-surface-3 p-4 text-[13px]">
                        <code className="whitespace-pre-wrap font-mono text-foreground">{action.content}</code>
                    </pre>
                </div>

                {/* Action buttons */}
                <div className="flex justify-end gap-2 pt-2">
                    <Button
                        variant="outline"
                        onClick={handleReject}
                        disabled={isExecuting}
                        className="gap-2"
                    >
                        <X className="h-4 w-4" />
                        Refuser
                    </Button>
                    <Button
                        onClick={handleApprove}
                        disabled={isExecuting}
                        className="gap-2"
                    >
                        {isExecuting ? (
                            <PxMark state="generating" size={14} />
                        ) : (
                            <Check className="h-4 w-4" />
                        )}
                        {isExecuting ? 'Exécution…' : 'Approuver'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Hook to parse Supabase actions from AI response
export function useSupabaseActionParser() {
    const parseActions = (content: string): SupabaseAction[] => {
        const actions: SupabaseAction[] = [];

        // Regex patterns for different action types
        const patterns = [
            { type: 'supabase-sql', regex: /<boltAction\s+type="supabase-sql"[^>]*>([\s\S]*?)<\/boltAction>/g },
            { type: 'supabase-create-table', regex: /<boltAction\s+type="supabase-create-table"\s+name="([^"]+)"[^>]*>([\s\S]*?)<\/boltAction>/g },
            { type: 'supabase-alter-table', regex: /<boltAction\s+type="supabase-alter-table"\s+name="([^"]+)"[^>]*>([\s\S]*?)<\/boltAction>/g },
            { type: 'supabase-policy', regex: /<boltAction\s+type="supabase-policy"\s+table="([^"]+)"[^>]*>([\s\S]*?)<\/boltAction>/g },
            { type: 'supabase-edge-function', regex: /<boltAction\s+type="supabase-edge-function"\s+name="([^"]+)"[^>]*>([\s\S]*?)<\/boltAction>/g },
            { type: 'supabase-logs', regex: /<boltAction\s+type="supabase-logs"(?:\s+source="([^"]*)")?(?:\s+limit="(\d+)")?\s*\/>/g },
            { type: 'supabase-schema', regex: /<boltAction\s+type="supabase-schema"\s*\/>/g },
            // NEW ADMIN ACTIONS
            { type: 'supabase-admin-operation', regex: /<boltAction\s+type="supabase-admin-operation"\s+table="([^"]+)"\s+operation="([^"]+)"[^>]*>([\s\S]*?)<\/boltAction>/g },
            { type: 'supabase-auth-users', regex: /<boltAction\s+type="supabase-auth-users"\s+action="([^"]+)"[^>]*>([\s\S]*?)<\/boltAction>/g },
            { type: 'supabase-email-template', regex: /<boltAction\s+type="supabase-email-template"[^>]*>([\s\S]*?)<\/boltAction>/g },
        ];

        patterns.forEach(({ type, regex }) => {
            let match;
            while ((match = regex.exec(content)) !== null) {
                const action: SupabaseAction = {
                    id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    type: type as SupabaseAction['type'],
                    content: '',
                };

                switch (type) {
                    case 'supabase-sql':
                        action.content = match[1].trim();
                        break;
                    case 'supabase-create-table':
                    case 'supabase-alter-table':
                        action.name = match[1];
                        action.content = match[2].trim();
                        break;
                    case 'supabase-policy':
                        action.table = match[1];
                        action.content = match[2].trim();
                        break;
                    case 'supabase-edge-function':
                        action.name = match[1];
                        action.content = match[2].trim();
                        break;
                    case 'supabase-logs':
                        action.source = match[1] || 'edge-functions';
                        action.limit = match[2] ? parseInt(match[2]) : 50;
                        action.content = `GET logs: ${action.source} (limit: ${action.limit})`;
                        break;
                    case 'supabase-schema':
                        action.content = 'GET database schema';
                        break;
                    case 'supabase-admin-operation':
                        action.table = match[1];
                        action.operation = match[2] as any;
                        action.content = match[3].trim();
                        break;
                    case 'supabase-auth-users':
                        action.operation = match[1] as any; // Using operation field for 'action'
                        action.content = match[2].trim();
                        break;
                    case 'supabase-email-template':
                        action.content = match[1].trim();
                        break;
                }

                if (action.content) {
                    actions.push(action);
                }
            }
        });

        return actions;
    };

    return { parseActions };
}

export default SupabaseActionApproval;
